/**
 * Conversation Sidebar - List of conversations with rename support
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, MessageSquare, Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/v2/ui';
import { useDeleteConversation, useRenameConversation } from '@/hooks/useChatImage';
import type { ChatConversation } from '@/lib/api/chat-image';

interface Props {
  conversations: ChatConversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  isLoading: boolean;
}

export function ConversationSidebar({ conversations, activeId, onSelect, onNew, isLoading }: Props) {
  const { t } = useTranslation();

  return (
    <div className="w-64 border-r border-neutral-200 dark:border-neutral-800 flex flex-col bg-white dark:bg-neutral-900">
      <div className="p-3 border-b border-neutral-200 dark:border-neutral-800">
        <Button onClick={onNew} variant="primary" size="sm" className="w-full" iconLeft={<Plus className="h-4 w-4" />}>
          {t('chatImage.newConversation')}
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading && <div className="p-4 text-sm text-neutral-500">{t('common.loading')}</div>}
        {conversations.map((conv) => (
          <ConversationItem
            key={conv.id}
            conv={conv}
            isActive={activeId === conv.id}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

interface ItemProps {
  conv: ChatConversation;
  isActive: boolean;
  onSelect: (id: string) => void;
}

function ConversationItem({ conv, isActive, onSelect }: ItemProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(conv.title || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const deleteMutation = useDeleteConversation();
  const renameMutation = useRenameConversation();

  // eslint-disable-next-line react-hooks/set-state-in-effect -- sync title from prop
  useEffect(() => { setTitle(conv.title || ''); }, [conv.title]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const handleRenameSubmit = () => {
    const trimmed = title.trim();
    setEditing(false);
    if (trimmed && trimmed !== conv.title) {
      renameMutation.mutate({ conversationId: conv.id, title: trimmed });
    }
  };

  if (editing) {
    return (
      <div className="px-3 py-2.5 border-b border-neutral-100 dark:border-neutral-800">
        <input
          ref={inputRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleRenameSubmit();
            if (e.key === 'Escape') setEditing(false);
          }}
          maxLength={60}
          className="w-full text-sm px-2 py-1 rounded border border-primary-400 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none"
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => onSelect(conv.id)}
      className={`w-full text-left px-3 py-2.5 border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors group ${
        isActive ? 'bg-neutral-100 dark:bg-neutral-800' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-neutral-400 shrink-0" />
        <span className="text-sm truncate flex-1 text-neutral-900 dark:text-neutral-100">
          {conv.title || t('chatImage.untitled')}
        </span>
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            className="p-1 hover:text-primary-500"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(conv.id); }}
            className="p-1 hover:text-red-500"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
      <div className="text-xs text-neutral-400 mt-0.5 ml-6">
        {conv.message_count} {t('chatImage.messages')} · {conv.total_images} {t('chatImage.images')}
      </div>
    </button>
  );
}
